import { memo, useCallback, useMemo } from "react";

import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useSessionContext } from "@/contexts/SessionContext";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import RefreshIcon from "@mui/icons-material/Refresh";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { SendWhatsAppMessageDialog } from "src-next/modules/crm-clinical/messaging";
import SectionHeader from "src-next/shared/ui/sections/SectionHeader";

import type { SectionDragHandleProps } from "../../management/types";
import { useCommunicationsWhatsAppSendDialog } from "../hooks/useCommunicationsWhatsAppSendDialog";
import { resolveCrmOrganizationId } from "../helpers/resolveCrmOrganizationId";
import { useClinicalCrmCommunicationsViewModel } from "../useClinicalCrmCommunicationsViewModel";
import CommunicationsBatchHistoryPanel from "./CommunicationsBatchHistoryPanel";
import CommunicationsFocusPanel from "./CommunicationsFocusPanel";
import CommunicationsShortcutsPanel from "./CommunicationsShortcutsPanel";
import { Insights } from "@mui/icons-material";

export interface ClinicalCrmCommunicationsSectionProps {
  isCollapsed: boolean;
  onToggleCollapsed: () => void;
  dragHandleProps: SectionDragHandleProps;
  /** ID da organização (CNPJ) — necessário para buscar Basic e Task. */
  organizationId?: string;
  /** Incrementar após envio de mensagem para recarregar histórico. */
  statsRefreshSignal?: number;
  practitionerId?: string;
  /** Após envio bem-sucedido (ex.: atualizar estatísticas / histórico). */
  onMessageSent?: () => void;
  hasActiveMetaEndpoint?: boolean;
}

/**
 * Seção de gestão de comunicações (FluxZap): acesso rápido, histórico de disparos
 * e análise por modelo ou por paciente.
 */
const ClinicalCrmCommunicationsSection = memo(
  ({
    isCollapsed,
    onToggleCollapsed,
    dragHandleProps,
    organizationId,
    statsRefreshSignal = 0,
    practitionerId,
    onMessageSent,
    hasActiveMetaEndpoint,
  }: ClinicalCrmCommunicationsSectionProps) => {
    const { access } = useSessionContext();
    const { organizationId: contextOrganizationId } = useOrganizationContext();

    /** Prop da página ou contexto da sessão (FluxZap / CRM sem injeção explícita). */
    const effectiveOrganizationId = useMemo(
      () =>
        resolveCrmOrganizationId(
          organizationId,
          contextOrganizationId,
          access?.organizationId,
        ),
      [organizationId, contextOrganizationId, access?.organizationId],
    );

    const vm = useClinicalCrmCommunicationsViewModel({
      organizationId: effectiveOrganizationId,
      refreshSignal: statsRefreshSignal,
    });

    const { openSendMessage, sendDialogProps } =
      useCommunicationsWhatsAppSendDialog({
        organizationId: effectiveOrganizationId,
        practitionerId,
        onMessageSent,
        franchiseRefreshSignal: statsRefreshSignal,
        hasActiveMetaEndpoint,
      });

    const handleRefreshClick = useCallback(() => {
      void vm.refetch();
    }, [vm.refetch]);

    return (
      <Box>
        <SectionHeader
          icon={<Insights />}
          title="Insights"
          subtitle="Acesso rápido a listas de pacientes, histórico de disparos e análise por modelo ou por pessoa."
          isCollapsed={isCollapsed}
          onCollapsedChange={() => onToggleCollapsed()}
          actions={
            <Stack direction="row" spacing={0.5} alignItems="center">
              {vm.hasOrganization ? (
                <Tooltip title="Atualizar comunicações e histórico">
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Atualizar comunicações"
                      onClick={handleRefreshClick}
                      disabled={vm.isLoading}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null}
              <IconButton
                size="small"
                aria-label="Arrastar para reordenar a seção na página"
                sx={{ cursor: "grab" }}
                {...(dragHandleProps?.attributes ?? {})}
                {...(dragHandleProps?.listeners ?? {})}
              >
                <DragIndicatorIcon />
              </IconButton>
            </Stack>
          }
        />

        {!isCollapsed ? (
          <Box
            sx={{
              mt: 1.5,
              p: { xs: 1.25, sm: 1.5 },
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "action.hover",
            }}
          >
            <Stack spacing={2}>
              {!vm.hasOrganization ? (
                <Typography variant="body2" color="text.secondary">
                  Informe o identificador da organização para carregar
                  comunicações e histórico de disparos.
                </Typography>
              ) : null}

              {vm.loadError != null ? (
                <Alert severity="warning" variant="outlined" sx={{ py: 0.5 }}>
                  Não foi possível carregar todas as comunicações. Use o botão
                  de atualizar para tentar novamente.
                </Alert>
              ) : null}

              {vm.isLoading && vm.hasOrganization ? (
                <Stack spacing={1}>
                  <Skeleton variant="rounded" height={48} />
                  <Skeleton variant="rounded" height={120} />
                  <Skeleton variant="rounded" height={200} />
                </Stack>
              ) : null}

              {!vm.isLoading && vm.hasOrganization ? (
                <>
                  <CommunicationsFocusPanel
                    focusMode={vm.focusMode}
                    onFocusModeChange={vm.setFocusMode}
                    templateRows={vm.templateRows}
                    selectedTemplateCode={vm.selectedTemplateCode}
                    onSelectTemplateCode={vm.setSelectedTemplateCode}
                    selectedTemplateMessages={vm.selectedTemplateMessages}
                    resolvePatientForMessage={vm.resolvePatientForMessage}
                    patientMessageGroups={vm.patientMessageGroups}
                    vetApp={vm.vetApp}
                    petNamesByTutorLogicalId={vm.petNamesByTutorLogicalId}
                    onOpenSendMessage={openSendMessage}
                  />

                  <CommunicationsBatchHistoryPanel
                    batchTasks={vm.batchTasks}
                    patientLookup={vm.patientLookup}
                    vetApp={vm.vetApp}
                    petNamesByTutorLogicalId={vm.petNamesByTutorLogicalId}
                    onOpenSendMessage={openSendMessage}
                  />
                </>
              ) : null}
            </Stack>
          </Box>
        ) : null}

        <SendWhatsAppMessageDialog {...sendDialogProps} />
      </Box>
    );
  },
);

ClinicalCrmCommunicationsSection.displayName =
  "ClinicalCrmCommunicationsSection";

export default ClinicalCrmCommunicationsSection;
